import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  where,
  deleteDoc,
  updateDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../../db/firebase.config";

export default class EventManager {
  static MAX_EVENTS = 10;

  static async getEvents() {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500)); //Delay to reduce requests frequency

      const eventsSnapshot = await getDocs(
        query(
          collection(db, "events"),
          orderBy("startDate", "asc"),
          limit(EventManager.MAX_EVENTS)
        )
      );
      const events = [];

      for (const eventDoc of eventsSnapshot.docs) {
        const eventData = eventDoc.data();

        const titlesSnapshot = await getDocs(
          query(
            collection(db, "eventsTitles"),
            where("eventId", "==", doc(db, "events", eventDoc.id))
          )
        );

        const titles = {};
        titlesSnapshot.forEach((titleDoc) => {
          const titleData = titleDoc.data();
          titles[titleData.lang] = titleData.text;
        });

        const event = {
          titles,
          descriptions: {
            uk: eventData?.descriptions?.uk || "",
            en: eventData?.descriptions?.en || "",
          },
          link: eventData.link,
          dates: {
            start: eventData.startDate.toDate(),
            end: eventData.endDate.toDate(),
          },
        };

        events.push({ id: eventDoc.id, ...event });
      }

      return events;
    } catch (error) {
      console.error("Error getting events:", error);
    }
  }

  static async addEvent({ titles, descriptions = {}, link, dates }) {
    if (!(titles.uk || titles.en || dates.start || dates.end || link))
      throw new Error("No titles or dates provided");

    const currentEventsSnapshot = await getDocs(collection(db, "events"));
    if (currentEventsSnapshot.size >= EventManager.MAX_EVENTS) {
      throw new Error(`Events limit reached (${EventManager.MAX_EVENTS})`);
    }

    const newEvent = {
      startDate: new Date(dates.start),
      endDate: new Date(dates.end),
      link: link,
      descriptions: {
        uk: descriptions.uk || "",
        en: descriptions.en || "",
      },
    };

    try {
      const eventRef = await addDoc(collection(db, "events"), newEvent);
      for (const [key, value] of Object.entries(titles)) {
        const titleDto = {
          eventId: eventRef,
          lang: key,
          text: value,
        };
        await addDoc(collection(db, "eventsTitles"), titleDto);
      }
    } catch (error) {
      console.error("Error adding event:", error);
      throw error;
    }
  }

  static async updateEvent(id, newData) {
    try {
      if (
        newData.titles.uk &&
        newData.titles.en &&
        newData.dates.start &&
        newData.dates.end &&
        newData.link
      ) {
      }
      const { titles, descriptions = {}, ...eventData } = newData;

      const eventRef = doc(db, "events", id);

      if (Object.keys(eventData).length > 0) {
        await updateDoc(eventRef, {
          startDate: new Date(eventData.dates.start),
          endDate: new Date(eventData.dates.end),
          link: eventData.link,
          descriptions: {
            uk: descriptions.uk || "",
            en: descriptions.en || "",
          },
        });
      }

      if (titles) {
        for (const [lang, text] of Object.entries(titles)) {
          const titleQuery = query(
            collection(db, "eventsTitles"),
            where("eventId", "==", eventRef),
            where("lang", "==", lang)
          );

          const titleSnapshot = await getDocs(titleQuery);

          const { id } = titleSnapshot.docs[0];

          await updateDoc(doc(db, "eventsTitles", id), {
            text: text,
          });
        }
      }
    } catch (e) {
      console.error("Error updating event:", e);
      throw e;
    }
  }

  static async deleteEvent(id) {
    try {
      const eventRef = doc(collection(db, "events"), id);

      const titlesQuerySnapshot = await getDocs(
        query(collection(db, "eventsTitles"), where("eventId", "==", eventRef))
      );

      await deleteDoc(eventRef);

      titlesQuerySnapshot.forEach(async (docSnapshot) => {
        await deleteDoc(doc(db, "eventsTitles", docSnapshot.id));
      });
      return "success";
    } catch (e) {
      return "fail";
    }
  }
}
